import type { ReconciliationPeriod } from "@/store/extractionAnnualized/reconciliation";
import React from "react";
import clsx from "clsx";

import { endOfMonth, format, startOfMonth } from "date-fns";

import { Button, Icon, IconBox } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Loader from "@/ui/atoms/Loader";
import InfoMessage from "@/ui/atoms/InfoMessage";

import type { AgGridReact } from "@/ui/molecules/Grid";
import { parseDateIfItsValidAndMatchesFormat } from "@/ui/molecules/MonthsBasedDatePicker";

import type { LayoutFillParentCenteredProps } from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { TDateRange } from "@/ui/organisms/MonthBasedRangePicker";
import type { GridReviewProps } from "@/ui/organisms/GridReview";
import type { TColumnDefsOperatingStatementView } from "@/ui/organisms/typesGrids";
import MonthBasedRangePicker from "@/ui/organisms/MonthBasedRangePicker";
import GridAnnualization from "@/ui/organisms/GridAnnualization";
import { AnnualizationGroupSettings } from "@/ui/organisms/AnnualizationGroupSettings";
import ABTLCollapsible, {
    testId as ABTLCollapsibleTestId,
} from "@/ui/organisms/ABTLCollapsible";

import { formatNumber } from "@/ui/organisms/utils";

import type { TAnnualizationType } from "@/apis/odin/apiODIN";
import Typography from "@/ui/atoms/Typography";
import VerticalDivider from "@/ui/molecules/VerticalDivider";
import { assertNever } from "@/utils/guards";

const annualizationTypeLabel = (annualizationType: TAnnualizationType) => {
    switch (annualizationType) {
        case "monthly_actual":
            return "Monthly Actuals";
        case "monthly_budget":
            return "Monthly Budgets";
        case "yearly_actual":
            return "Yearly Actuals";
        case "yearly_budget":
            return "Yearly Budgets";
        default:
            return assertNever(annualizationType);
    }
};

export type ViewExtractionAnnualizedParams = {
    getCurrentDate: () => Date;
    useState: (params: { projectId: number; databaseId: number }) => {
        loadAnnualizedExtractionStatus:
            | "idle"
            | "pending"
            | "resolved"
            | "rejected";
        loadAnnualizedExtractionPeriodsStatus:
            | "idle"
            | "pending"
            | "resolved"
            | "rejected";
        availablePeriods:
            | {
                  periodStart: string;
                  periodEnd: string;
              }[]
            | null;
        columnDefsOperatingStatements: TColumnDefsOperatingStatementView | null;
        rows: GridReviewProps["reviewData"] | null;
        nonOperatingRows: GridReviewProps["reviewData"] | null;
        totalNetIncome: { [key: string]: number } | null;
        extractionsInfo:
            | {
                  fileName: string;
                  extractionId: number;
                  sheetName: string;
                  uploadedAt: string | undefined;
                  uploadedBy: string | undefined;
                  completedAt: string | null | undefined;
              }[]
            | null;
        extractionAnnaualizedStartDate: string | null;
        extractionAnnaualizedEndDate: string | null;
        selectedAnnualizationGroup: "month" | "quarter" | "half" | "year";
        annualizationType: TAnnualizationType;
        showSource: boolean;
        showReconciliation?: boolean;
        reconciliationAvailable?: boolean;
        reconciliationPeriods?: ReconciliationPeriod[];
    };
    useCases: () => {
        onUnmount: () => void;
        onToggleReconciliation?: (show: boolean) => void;
        onDateRangeChange: (
            params: TDateRange & { projectId: number; databaseId: number },
        ) => void;
        onDateRangeChangeError: (message: string) => void;
        onExportAnnualizationClicked: (
            params: TDateRange & {
                projectId: number;
                databaseId: number;
                interval: "month" | "quarter" | "half" | "year";
                annualizationType: TAnnualizationType;
                reconciliation?: boolean;
            },
        ) => void;
        onChangeSelectedAnnualizationGroup: (params: {
            projectId: number;
            databaseId: number;
            group: "month" | "quarter" | "half" | "year";
        }) => void;
        onToggleShowSource: (showSource: boolean) => void;
        onChangeAnnualizationType: (params: {
            type: TAnnualizationType;
        }) => void;
    };
};

export type ViewExtractionAnnualizedProps = LayoutFillParentCenteredProps & {
    projectId: number;
    databaseId: number;
    onViewExtraction: ({
        projectId,
        extractionId,
    }: {
        projectId: number;
        extractionId: number;
    }) => void;
    getExtractionReviewUrl: (params: {
        projectId: number;
        extractionId: number;
    }) => string;
};

export type ViewExtractionAnnualizedOptionsProps = {
    projectId: number;
    databaseId: number;
};

export type ViewExtractionAnnualizedAdditionalOptionsProps = {
    projectId: number;
    databaseId: number;
};

export const testIds = {
    main: "ViewExtractionAnnualized",
    datePicker: "ViewExtractionAnnualizedDatePicker",
    exportButton: "ViewExtractionAnnualizedExportButton",
    belowTheLineGrid: "ViewExtractionAnnualizedBelowTheLineGrid",
    belowTheLineGridToggle: ABTLCollapsibleTestId,
    showSourceButton: "ViewExtractionAnnualizedShowSourceButton",
    actualButton: "ViewExtractionAnnualizedActualButton",
    budgetButton: "ViewExtractionAnnualizedBudgetButton",
    reconciliationButton: "ViewExtractionAnnualizedReconciliationButton",
    monthlyButton: "ViewExtractionAnnualizedMonthlyButton",
    yearlyButton: "ViewExtractionAnnualizedYearlyButton",
};

export const createViewExtractionAnnualized = ({
    getCurrentDate,
    useState,
    useCases,
}: ViewExtractionAnnualizedParams) => {
    const ViewExtractionAnnualizedOptions: React.FC<
        ViewExtractionAnnualizedOptionsProps
    > = ({ projectId, databaseId }) => {
        const {
            onDateRangeChange,
            onDateRangeChangeError,
            onChangeSelectedAnnualizationGroup,
        } = useCases();
        const {
            loadAnnualizedExtractionStatus,
            loadAnnualizedExtractionPeriodsStatus,
            availablePeriods,
            selectedAnnualizationGroup,
            annualizationType,
        } = useState({ projectId, databaseId });

        const currentDate = React.useMemo(() => getCurrentDate(), []);
        const currentDateFormatted = format(currentDate, "MM/yyyy");
        const annualizationPending =
            loadAnnualizedExtractionStatus === "pending" ||
            loadAnnualizedExtractionPeriodsStatus === "pending";

        const transformRangeToOutputFormat = React.useCallback(
            (value: TDateRange) => {
                const startDate = parseDateIfItsValidAndMatchesFormat({
                    dateString: value.startDate,
                    formatString: "MM/yyyy",
                });
                const endDate = parseDateIfItsValidAndMatchesFormat({
                    dateString: value.endDate,
                    formatString: "MM/yyyy",
                });
                if (startDate === false || endDate === false) {
                    throw new Error("Invalid date format");
                }
                const startDateStringWithDays = format(
                    startOfMonth(startDate),
                    "yyyy-MM-dd",
                );
                const endDateStringWithDays = format(
                    endOfMonth(endDate),
                    "yyyy-MM-dd",
                );
                return {
                    startDate: startDateStringWithDays,
                    endDate: endDateStringWithDays,
                };
            },
            [],
        );
        const firstAvailablePeriod = availablePeriods
            ? availablePeriods.reduce(
                  (min, period) => {
                      return new Date(period.periodStart) <
                          new Date(min.periodStart)
                          ? period
                          : min;
                  },
                  availablePeriods[0] as {
                      periodStart: string;
                      periodEnd: string;
                  },
              )
            : undefined;

        const lastAvailablePeriod = availablePeriods
            ? availablePeriods.reduce(
                  (max, period) => {
                      return new Date(period.periodEnd) >
                          new Date(max.periodEnd)
                          ? period
                          : max;
                  },
                  availablePeriods[0] as {
                      periodStart: string;
                      periodEnd: string;
                  },
              )
            : undefined;
        const monthRangePickerLastEndDate =
            firstAvailablePeriod?.periodStart !== undefined
                ? format(new Date(firstAvailablePeriod?.periodStart), "MM/yyyy")
                : undefined;
        const monthRangePickerCurrentDate =
            lastAvailablePeriod?.periodEnd !== undefined
                ? format(new Date(lastAvailablePeriod?.periodEnd), "MM/yyyy")
                : currentDateFormatted;
        const highlightedEndDates = React.useMemo(
            () =>
                (
                    availablePeriods?.flatMap(period => {
                        return [
                            format(new Date(period.periodStart), "MM/yyyy"),
                            format(new Date(period.periodEnd), "MM/yyyy"),
                        ];
                    }) || []
                ).filter(
                    (dateString, i, array) => array.indexOf(dateString) === i,
                ),
            [availablePeriods],
        );

        const loadAnnualizedExtractionPeriodsRequestCompleted =
            loadAnnualizedExtractionPeriodsStatus === "resolved" ||
            loadAnnualizedExtractionPeriodsStatus === "rejected";
        const isYearColumnsAnnualizationType =
            annualizationType === "yearly_actual" ||
            annualizationType === "yearly_budget";

        return loadAnnualizedExtractionPeriodsRequestCompleted ? (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    alignItems: "center",
                }}
            >
                <MonthBasedRangePicker
                    disabled={loadAnnualizedExtractionStatus === "pending"}
                    currentDate={monthRangePickerCurrentDate}
                    lastEndDate={monthRangePickerLastEndDate}
                    highlightedEndDates={highlightedEndDates}
                    showOutputMonth={!isYearColumnsAnnualizationType}
                    showOutputYear={isYearColumnsAnnualizationType}
                    resetOutputMonthKey={annualizationType}
                    onChange={({ endDate, startDate }) =>
                        onDateRangeChange({
                            projectId,
                            endDate,
                            startDate,
                            databaseId,
                        })
                    }
                    onError={onDateRangeChangeError}
                    transformRangeToOutputFormat={transformRangeToOutputFormat}
                />
                {annualizationType !== "yearly_actual" &&
                    annualizationType !== "yearly_budget" && (
                        <AnnualizationGroupSettings
                            selectedGroup={selectedAnnualizationGroup}
                            onChangeSelectedGroup={group =>
                                onChangeSelectedAnnualizationGroup({
                                    projectId,
                                    databaseId,
                                    group,
                                })
                            }
                            periodButtonsDisabled={annualizationPending}
                        />
                    )}
            </Box>
        ) : (
            <Box />
        );
    };

    const ViewExtractionAnnualizedAdditionalOptions: React.FC<
        ViewExtractionAnnualizedAdditionalOptionsProps
    > = props => {
        const { projectId, databaseId } = props;
        const {
            onToggleReconciliation,
            onToggleShowSource,
            onChangeAnnualizationType,
            onExportAnnualizationClicked,
        } = useCases();

        const {
            showReconciliation = false,
            reconciliationAvailable = false,
            showSource,
            annualizationType,
            selectedAnnualizationGroup,
            rows,
            loadAnnualizedExtractionStatus,
            loadAnnualizedExtractionPeriodsStatus,
            extractionAnnaualizedStartDate,
            extractionAnnaualizedEndDate,
        } = useState({ projectId, databaseId });

        const annualizationValue: "actual" | "budget" =
            annualizationType.includes("_budget") ? "budget" : "actual";
        const annualizationPeriod: "monthly" | "yearly" =
            annualizationType.startsWith("yearly_") ? "yearly" : "monthly";
        const composeAnnualizationType = React.useCallback(
            ({
                period,
                value,
            }: {
                period: "monthly" | "yearly";
                value: "actual" | "budget";
            }): TAnnualizationType =>
                `${period}_${value}` as TAnnualizationType,
            [],
        );

        const isLoading =
            loadAnnualizedExtractionStatus === "idle" ||
            loadAnnualizedExtractionStatus === "pending" ||
            loadAnnualizedExtractionPeriodsStatus === "pending" ||
            loadAnnualizedExtractionPeriodsStatus === "idle";

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    height: "1.5rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "12px",
                        alignItems: "center",
                        width: "max-content",
                    }}
                >
                    <Typography variant="body3">Statement Type:</Typography>
                    <Button
                        data-testid={testIds.actualButton}
                        pattern={
                            !showReconciliation &&
                            annualizationValue === "actual"
                                ? "primary"
                                : "secondary"
                        }
                        size="small"
                        onClick={() => {
                            onToggleReconciliation?.(false);
                            onChangeAnnualizationType({
                                type: composeAnnualizationType({
                                    period: annualizationPeriod,
                                    value: "actual",
                                }),
                            });
                        }}
                        disabled={isLoading}
                        isInline
                    >
                        Actual
                    </Button>
                    <Button
                        data-testid={testIds.budgetButton}
                        pattern={
                            !showReconciliation &&
                            annualizationValue === "budget"
                                ? "primary"
                                : "secondary"
                        }
                        size="small"
                        onClick={() => {
                            onToggleReconciliation?.(false);
                            onChangeAnnualizationType({
                                type: composeAnnualizationType({
                                    period: annualizationPeriod,
                                    value: "budget",
                                }),
                            });
                        }}
                        disabled={isLoading}
                        isInline
                    >
                        Budget
                    </Button>
                    <Box
                        component="span"
                        title={
                            reconciliationAvailable
                                ? "Compare actuals with budget"
                                : "Needs both actual and budget figures."
                        }
                        sx={{
                            display: "inline-flex",
                            opacity: reconciliationAvailable ? 1 : 0.45,
                        }}
                    >
                        <Button
                            data-testid={testIds.reconciliationButton}
                            pattern={
                                showReconciliation ? "primary" : "secondary"
                            }
                            size="small"
                            isInline
                            disabled={isLoading || !reconciliationAvailable}
                            onClick={() => onToggleReconciliation?.(true)}
                        >
                            Reconciliation
                        </Button>
                    </Box>
                    <VerticalDivider />
                    <Button
                        data-testid={testIds.monthlyButton}
                        pattern={
                            annualizationPeriod === "monthly"
                                ? "primary"
                                : "secondary"
                        }
                        size="small"
                        onClick={() =>
                            onChangeAnnualizationType({
                                type: composeAnnualizationType({
                                    period: "monthly",
                                    value: annualizationValue,
                                }),
                            })
                        }
                        disabled={isLoading}
                        isInline
                    >
                        Monthly
                    </Button>
                    <Button
                        data-testid={testIds.yearlyButton}
                        pattern={
                            annualizationPeriod === "yearly"
                                ? "primary"
                                : "secondary"
                        }
                        size="small"
                        onClick={() =>
                            onChangeAnnualizationType({
                                type: composeAnnualizationType({
                                    period: "yearly",
                                    value: annualizationValue,
                                }),
                            })
                        }
                        disabled={isLoading}
                        isInline
                    >
                        Yearly
                    </Button>

                    {!showReconciliation &&
                        selectedAnnualizationGroup === "month" &&
                        annualizationPeriod !== "yearly" && (
                            <>
                                <VerticalDivider />
                                <Button
                                    data-testid={testIds.showSourceButton}
                                    pattern="secondary"
                                    isGhost
                                    onClick={() =>
                                        onToggleShowSource(!showSource)
                                    }
                                    size="medium"
                                    isInline
                                    disabled={isLoading}
                                >
                                    <IconBox mr={0.5}>
                                        <Icon
                                            icon={
                                                showSource ? "eye-slash" : "eye"
                                            }
                                            size="lg"
                                            color={
                                                showSource
                                                    ? "var(--proda-black-10)"
                                                    : "var(--proda-neon-blue)"
                                            }
                                        />
                                    </IconBox>
                                    {showSource
                                        ? "Hide Sources"
                                        : "Show Sources"}
                                </Button>
                            </>
                        )}
                </Box>

                <Button
                    pattern="secondary"
                    size="small"
                    leadingIcon="download"
                    isInline={true}
                    onClick={() => {
                        if (
                            extractionAnnaualizedStartDate !== null &&
                            extractionAnnaualizedEndDate !== null
                        ) {
                            onExportAnnualizationClicked({
                                projectId,
                                databaseId,
                                startDate: extractionAnnaualizedStartDate,
                                endDate: extractionAnnaualizedEndDate,
                                interval:
                                    showReconciliation &&
                                    annualizationPeriod === "yearly"
                                        ? "year"
                                        : selectedAnnualizationGroup,
                                annualizationType,
                                ...(showReconciliation
                                    ? { reconciliation: true }
                                    : {}),
                            });
                        }
                    }}
                    disabled={
                        loadAnnualizedExtractionStatus !== "resolved" ||
                        rows === null ||
                        rows.length === 0 ||
                        annualizationType === null
                    }
                    data-testid={testIds.exportButton}
                >
                    Export
                </Button>
            </Box>
        );
    };

    const ViewExtractionAnnualized: React.FC<
        ViewExtractionAnnualizedProps
    > = props => {
        const {
            sx,
            className,
            projectId,
            databaseId,
            getExtractionReviewUrl,
            onViewExtraction,
            ...otherProps
        } = props;

        const { onUnmount } = useCases();

        const {
            reconciliationPeriods,
            rows,
            nonOperatingRows,
            loadAnnualizedExtractionStatus,
            loadAnnualizedExtractionPeriodsStatus,
            columnDefsOperatingStatements,
            totalNetIncome,
            extractionsInfo,
            annualizationType,
            selectedAnnualizationGroup,
            showSource,
        } = useState({ projectId, databaseId });

        const operatingStatementsGridRef = React.useRef<AgGridReact>(null);
        const belowTheLineGridRef = React.useRef<AgGridReact>(null);
        const showBelowTheLineGrid =
            nonOperatingRows !== null && nonOperatingRows.length > 0;

        const longestOperatingStatementAndTotalsNumericValue = React.useMemo(
            () =>
                rows
                    ? rows.reduce((acc, row) => {
                          const isNumberTypeGuard = (
                              value: unknown,
                          ): value is number => typeof value === "number";
                          const numericValues =
                              Object.values(row).filter(isNumberTypeGuard);
                          const numericValuesFormattedLengths =
                              numericValues.map(
                                  value => formatNumber(value).length,
                              );
                          const longestValue = Math.max(
                              ...numericValuesFormattedLengths,
                          );
                          return Math.max(acc, longestValue);
                      }, 0)
                    : 0,
            [rows],
        );

        React.useEffect(() => {
            return () => onUnmount();
        }, [projectId, databaseId, onUnmount]);

        return (
            <LayoutFillParentCentered
                className={clsx([testIds.main, className])}
                sx={{ display: "flex", flexDirection: "column", ...sx }}
                {...otherProps}
            >
                {loadAnnualizedExtractionStatus === "rejected" ? (
                    <InfoMessage>
                        We encountered an issue while loading the annualized
                        view.
                    </InfoMessage>
                ) : loadAnnualizedExtractionStatus === "pending" ||
                  loadAnnualizedExtractionPeriodsStatus === "pending" ? (
                    <Loader />
                ) : loadAnnualizedExtractionStatus === "resolved" ? (
                    rows === null ? (
                        <InfoMessage>
                            There are no{" "}
                            {annualizationTypeLabel(annualizationType)} with
                            completed extractions.
                        </InfoMessage>
                    ) : rows.length === 0 ? (
                        <InfoMessage>
                            No data to display! Try to select different date
                            range.
                        </InfoMessage>
                    ) : (
                        <>
                            <GridAnnualization
                                ref={operatingStatementsGridRef}
                                reconciliationPeriods={reconciliationPeriods}
                                projectId={projectId}
                                alignedGrids={[belowTheLineGridRef]}
                                columnDefsOperatingStatements={
                                    columnDefsOperatingStatements || {}
                                }
                                extractionsInfo={extractionsInfo}
                                data={rows}
                                totalNetIncome={totalNetIncome}
                                showSource={showSource}
                                selectedAnnualizationGroup={
                                    selectedAnnualizationGroup
                                }
                                longestOperatingStatementAndTotalsNumericValue={
                                    longestOperatingStatementAndTotalsNumericValue
                                }
                                onViewExtraction={onViewExtraction}
                                getExtractionReviewUrl={getExtractionReviewUrl}
                            />
                            {showBelowTheLineGrid && (
                                <ABTLCollapsible>
                                    <GridAnnualization
                                        ref={belowTheLineGridRef}
                                        data-testid={testIds.belowTheLineGrid}
                                        className={testIds.belowTheLineGrid}
                                        reconciliationPeriods={
                                            reconciliationPeriods
                                        }
                                        projectId={projectId}
                                        alignedGrids={[
                                            operatingStatementsGridRef,
                                        ]}
                                        columnDefsOperatingStatements={
                                            columnDefsOperatingStatements || {}
                                        }
                                        extractionsInfo={extractionsInfo}
                                        data={nonOperatingRows}
                                        totalNetIncome={null}
                                        showSource={showSource}
                                        selectedAnnualizationGroup={
                                            selectedAnnualizationGroup
                                        }
                                        longestOperatingStatementAndTotalsNumericValue={
                                            longestOperatingStatementAndTotalsNumericValue
                                        }
                                        onViewExtraction={() => {}}
                                        getExtractionReviewUrl={() => ""}
                                        headerHeight={0}
                                        alwaysShowVerticalScroll={true}
                                        suppressCellFocus={true}
                                        sx={{
                                            "& .ag-header": {
                                                display: "none",
                                            },
                                        }}
                                    />
                                </ABTLCollapsible>
                            )}
                        </>
                    )
                ) : null}
            </LayoutFillParentCentered>
        );
    };

    return {
        ViewExtractionAnnualized,
        ViewExtractionAnnualizedOptions,
        ViewExtractionAnnualizedAdditionalOptions,
    };
};
